import React, { useEffect, useRef, useState, useCallback } from 'react';
import { GAME_EVENTS, emitGameEvent } from '../../utils/gameEventBus';
import ReportFlag from './ReportFlag';
import { chatGameApi } from './chatGameApi';

/**
 * WatchPartyTheater — synced YouTube player for watch parties.
 * Spec: WATCH_PARTY_SPEC_2026_07_10.md (repo root).
 *
 * Sync model: the remote holder's player is authoritative. Their client
 * heartbeats position/state to the backend (~10s), which persists and
 * rebroadcasts `watch_party_state` over WS. Viewers correct drift only when
 * they're >2s off the estimated live position, so playback never feels jerky.
 */

const DRIFT_TOLERANCE_S = 2;
const HEARTBEAT_INTERVAL_MS = 10 * 1000;
// Ask-for-the-remote window (Arc 5, Nina: AUTO-DENY on holder silence). The
// holder's prompt and the asker's wait both run 10s; the server keeps honoring
// grants for 2 extra seconds of clock-skew grace, then refuses.
const REMOTE_REQUEST_PROMPT_MS = 10 * 1000;

// Singleton loader for the YouTube IFrame API
let ytApiPromise = null;
function loadYouTubeApi() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (prev) prev();
      resolve(window.YT);
    };
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
  });
  return ytApiPromise;
}

// Estimated live position from the last authoritative state
function estimatePosition(wp) {
  if (!wp) return 0;
  const base = Number(wp.position_s) || 0;
  if (wp.state !== 'playing') return base;
  const elapsed = (Date.now() - new Date(wp.position_at).getTime()) / 1000;
  return base + Math.max(0, elapsed);
}

function formatTime(s) {
  if (!isFinite(s) || s < 0) s = 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${m}:${String(sec).padStart(2, '0')}`;
}

const WATCH_REACT_EMOJIS = ['😂', '🔥', '😱', '👏', '💀', '🍿'];

// 📺 Little TV-remote glyph (no remote emoji exists) — grab button + holder
// label (Nina 07-16: the remote should look like a remote, not a gamepad).
const RemoteIcon = ({ size = 11 }) => (
  <svg width={size} height={Math.round(size * 1.7)} viewBox="0 0 12 20" fill="none" aria-hidden="true" style={{ display: 'inline-block', flexShrink: 0 }}>
    <rect x="1" y="1" width="10" height="18" rx="2.5" fill="currentColor" opacity="0.9" />
    <circle cx="6" cy="5" r="1.6" fill="#000" opacity="0.55" />
    <rect x="3" y="9" width="2.4" height="2.4" rx="0.7" fill="#000" opacity="0.55" />
    <rect x="6.6" y="9" width="2.4" height="2.4" rx="0.7" fill="#000" opacity="0.55" />
    <rect x="3" y="13" width="2.4" height="2.4" rx="0.7" fill="#000" opacity="0.55" />
    <rect x="6.6" y="13" width="2.4" height="2.4" rx="0.7" fill="#000" opacity="0.55" />
  </svg>
);

// Reaction-overlay keyframes, injected once per page. Quips stack as a
// side chat-stream (Nina 07-11: vertical, like live chat — not danmaku);
// emojis rise from the bottom, spread across the width.
let bulletStylesInjected = false;
function injectBulletStyles() {
  if (bulletStylesInjected || typeof document === 'undefined') return;
  bulletStylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes wpQuipIn {
      from { opacity: 0; transform: translateY(12px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes wpQuipOut {
      to { opacity: 0; }
    }
    @keyframes wpEmojiRise {
      0%   { opacity: 0; transform: translateY(0) scale(0.6); }
      12%  { opacity: 1; transform: translateY(-16px) scale(1.2); }
      100% { opacity: 0; transform: translateY(-150px) scale(1); }
    }
    @keyframes wpSoundNudgeRing {
      0%, 100% { transform: scale(0.9); opacity: 0; }
      50%      { transform: scale(1.35); opacity: 0.6; }
    }
    /* Sound-nudge halo rides a pseudo-ring on transform/opacity only —
       an animated box-shadow here sat ON TOP of the playing video, forcing
       a paint per frame against a surface that changes every frame
       (perf 08-13; same rule as the LivestreamPanel static-shadow notes). */
    .wp-sound-nudge::after {
      content: ''; position: absolute; inset: -5px; border-radius: 50%;
      border: 3px solid rgba(206,147,216,0.75);
      animation: wpSoundNudgeRing 1.4s ease-in-out infinite;
      pointer-events: none;
    }
  `;
  document.head.appendChild(style);
}

const WatchPartyTheater = ({
  watchParty,
  isRemoteHolder,
  onControl,   // (state, position_s, video_id) — play/pause/seek
  onHeartbeat, // (position_s, state, video_id) — holder's ~10s authoritative clock
  onReact,     // (emoji) — ephemeral reaction bullet
  lastReaction, // latest incoming watch_reaction ({kind, emoji|text, sender_name, _id})
  onNext,      // (endedVideoId?) — advance; with an id it's an idempotent ended-report
  onStop,      // end the party
  onExit,      // local leave (party continues without you)
  onMinimize,  // shrink the theater to the slim stage bar (keeps playing; 07-17)
  ambient,     // host-mode auto-seat: start MUTED (no user gesture yet) + unmute overlay
  isPlayer,    // joined players can grab the remote; spectators watch only
  isMobile,
  THEME = {},
  gameId = null,   // 🛡️ report flag (MODERATION_P1): report the current video
  playerId = null,
  maxVideoHeight = null, // 📏 stage resize bar (px) — null = the 40/55vh defaults
  rail = null,     // 🛋️ 09-23 batch 1: a node for the rail slot during a SCREENING (the couch column)
}) => {
  const hostRef = useRef(null);        // div the YT player mounts into
  const playerRef = useRef(null);      // YT.Player instance
  const playerReadyRef = useRef(false);
  const loadedVideoIdRef = useRef(null);
  const wpRef = useRef(watchParty);
  wpRef.current = watchParty;
  const isHolderRef = useRef(isRemoteHolder);
  isHolderRef.current = isRemoteHolder;
  // Position of the holder's last SENT heartbeat — a "playing" clock that
  // hasn't moved since the previous beat is an ad or a stall, not truth
  const lastBeatPosRef = useRef(null);

  const [displayPos, setDisplayPos] = useState(0);
  const [duration, setDuration] = useState(0);
  const [localState, setLocalState] = useState('playing');
  // 🧭 Free roam (scrub decouple): a non-holder who deliberately pauses or
  // scrubs leaves the party clock until Catch up / the next video. Ref mirror
  // because reconcileToParty is a stable callback.
  const [freeRoam, setFreeRoam] = useState(false);
  const freeRoamRef = useRef(false);
  freeRoamRef.current = freeRoam;
  const [showQueue, setShowQueue] = useState(false);
  // Ambient auto-seat happens without a user gesture, so the player must start
  // muted (browser autoplay policy) — the overlay button is the gesture.
  // EXCEPT builder-curated parties (D6): the builder explicitly chose this
  // soundtrack, so we ATTEMPT an unmuted start (works whenever the visitor
  // reached the space via a click — i.e. nearly always) and fall back to the
  // muted overlay only if the browser blocks it (hard loads, pasted URLs).
  const curatedStartRef = useRef(!!(ambient && watchParty?.curated));
  // 🔊 One-tap join (Nina 08-12): auto-seat is retired everywhere, so a
  // DESKTOP theater only ever mounts from a real click (Take a seat / Watch /
  // starting a party) — that gesture is spent on SOUND. Attempt an audible
  // start on every desktop mount; the onReady fallback below degrades to a
  // muted start when the browser blocks it (expired gesture, hard loads).
  // Mobile keeps the muted ambient start + overlay exactly as before.
  const audibleStartRef = useRef(curatedStartRef.current || !isMobile);
  const [needsUnmute, setNeedsUnmute] = useState(!!ambient && !audibleStartRef.current);
  // 🍿 Pill-tap intent (Nina 08-03): the pill renders from mount but the player
  // takes seconds to boot — a tap in that window used to no-op (unMute on a
  // not-ready player, caught) while still dismissing the pill, stranding the
  // desktop auto-seat in permanent silence. The tap now records intent here
  // and onReady delivers it.
  const pendingUnmuteRef = useRef(false);
  // 🔊 Local sound toggle — mirrors the player's mute so the couch can go
  // silent without leaving the party (Nina 07-16). Starts muted exactly when
  // the player itself does (mobile ambient non-curated autoplay).
  const [muted, setMuted] = useState(!!ambient && !audibleStartRef.current);
  // First real PLAYING event seen — gates the audible emit below so a blocked
  // autoplay (silent player) never reports the TV as making sound.
  const [everPlayed, setEverPlayed] = useState(false);

  // 📺→🎵 Audio arbiter (D6, reworked Nina 08-12): on DESKTOP, being SEATED at
  // a real party silences the music bed for the whole sit — pausing or muting
  // the TV no longer ping-pongs the music back in mid-party (the old
  // audibility gate restarted the bed on every pause and cut it on every
  // resume — THE "unintuitive dynamic"). Music returns, from the beginning,
  // when you leave the couch or the party ends (theater unmount → cleanup
  // emit → LofiRadio remounts its iframe). The ambient host-mode TV keeps the
  // walk-in-matrix rule: its muted programming must NOT silence the bed.
  // Mobile keeps the audibility gate everywhere (08-12: mobile untouched).
  const seatedOwnsAudio = !isMobile && !ambient;
  useEffect(() => {
    const audible = seatedOwnsAudio
      || (everPlayed && localState === 'playing' && !needsUnmute && !muted);
    emitGameEvent(GAME_EVENTS.WATCH_PARTY_PLAYING, { playing: audible });
  }, [seatedOwnsAudio, everPlayed, localState, needsUnmute, muted]);
  useEffect(() => () => {
    // Couch left / party over — hand the room's audio back to the music bed
    emitGameEvent(GAME_EVENTS.WATCH_PARTY_PLAYING, { playing: false });
  }, []);

  // 🔇 Space master mute (Nina 08-02): the space's sound button silences the
  // TV too. Still NO auto-unmute (08-02 call stands — sound on a synced video
  // is a deliberate tap on the theater's own control), but the unmute half
  // re-offers the way back (Nina 08-03; since 08-12 that's the pulsing corner
  // 🔊, not the center pill): before this, master mute→unmute left a silent
  // TV whose only way back was an unmarked 34px corner button — the "watch
  // party sound disappeared" one-way door.
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.muted) {
        try { playerRef.current?.mute?.(); } catch (err) { /* player not ready */ }
        setMuted(true);
        return;
      }
      // DESKTOP ONLY (Nina 08-03): mobile keeps the 08-02 behavior exactly.
      try {
        if (!isMobile && playerRef.current?.isMuted?.()) setNeedsUnmute(true);
      } catch (err) { /* player not ready — the muted-start flow still governs */ }
    };
    document.addEventListener('game:space_audio_muted', handler);
    return () => document.removeEventListener('game:space_audio_muted', handler);
  }, [isMobile]);

  // ── Container-width responsiveness. The theater renders at wildly
  // different widths (stage, tab panel, phones → ultrawide) and media
  // queries can't see container width, so measure the root directly.
  const rootRef = useRef(null);
  const [containerW, setContainerW] = useState(null);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setContainerW(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Below this width the fixed side rail would starve the video — fold the
  // queue into the drawer (the same UI mobile uses) instead of squeezing
  // the player.
  const compact = isMobile || (containerW !== null && containerW < 700);
  // 🎬🍿 CINEMA (Nina 09-22, mid-film: "the video screen is so narrow vertically,
  // it needs to take up more room like in narrow desktop"): during a SCREENING
  // on desktop the 55dvh budget becomes 85dvh, a smaller stored drag cap loses
  // to it (CSS max()), and the Up-next rail is folded away (the projector is
  // locked, the rail only ever said "Nothing queued yet"). Ordinary parties
  // and phones keep every number they had.
  const cinema = !isMobile && !!(watchParty && watchParty.screening);
  const CINEMA_H = '85dvh';
  const rowCap = cinema
    ? (maxVideoHeight ? `max(${maxVideoHeight}px, ${CINEMA_H})` : CINEMA_H)
    : (maxVideoHeight ? `${maxVideoHeight}px` : (isMobile ? undefined : '55dvh'));
  const videoCapW = cinema
    ? (maxVideoHeight ? `calc(max(${maxVideoHeight}px, ${CINEMA_H}) * 16 / 9)` : `calc(${CINEMA_H} * 16 / 9)`)
    : (maxVideoHeight ? `calc(${maxVideoHeight}px * 16 / 9)` : (isMobile ? 'calc(40dvh * 16 / 9)' : 'calc(55dvh * 16 / 9)'));

  // ── Add-to-playlist from inside the theater (up-next rail / mobile drawer).
  // Rides the same watchparty:request event the media cards use — with a party
  // live it queues. Server validates the URL; this check just guides typing.
  const [queueUrl, setQueueUrl] = useState('');
  const [queueFlash, setQueueFlash] = useState(null);
  const queueFlashTimer = useRef(null);
  useEffect(() => () => clearTimeout(queueFlashTimer.current), []);
  const submitQueueUrl = useCallback(() => {
    const url = queueUrl.trim();
    if (!url) return;
    const looksYouTube = /(?:youtube\.com\/|youtu\.be\/)/.test(url);
    clearTimeout(queueFlashTimer.current);
    if (!looksYouTube) {
      setQueueFlash('YouTube links only!');
    } else {
      window.dispatchEvent(new CustomEvent('watchparty:request', { detail: { url } }));
      setQueueUrl('');
      setQueueFlash('Added to the playlist!');
    }
    queueFlashTimer.current = setTimeout(() => setQueueFlash(null), 2500);
  }, [queueUrl]);

  // ── Ask for the remote (Arc 5). A HUMAN actively holding gets asked, not
  // robbed: one pending_request at a time lives on watch_party (the backend
  // race-guards the claim). AUTO-DENY on 10s holder silence is LAZY server
  // side — no broadcast fires at expiry — so the asker's "no answer" outcome
  // resolves on a local timer; an explicit deny arrives sooner via broadcast.
  const [askFlash, setAskFlash] = useState(null);       // "Someone already asked"
  const [remoteToast, setRemoteToast] = useState(null); // soft ask-outcome toast
  const askFlashTimer = useRef(null);
  const remoteToastTimer = useRef(null);
  const askExpireTimer = useRef(null);
  const askedRef = useRef(false);   // an ask of OURS is in flight / parked
  const askSeenRef = useRef(false); // our ask was seen parked on party state
  useEffect(() => () => {
    clearTimeout(askFlashTimer.current);
    clearTimeout(remoteToastTimer.current);
    clearTimeout(askExpireTimer.current);
  }, []);

  const settleAsk = useCallback((granted) => {
    if (!askedRef.current) return;
    askedRef.current = false;
    askSeenRef.current = false;
    clearTimeout(askExpireTimer.current);
    if (!granted) {
      clearTimeout(remoteToastTimer.current);
      setRemoteToast('The remote stays put for now');
      remoteToastTimer.current = setTimeout(() => setRemoteToast(null), 4000);
    }
  }, []);

  const handleAskForRemote = useCallback(() => {
    if (!gameId || !playerId || askedRef.current) return;
    askedRef.current = true;
    askSeenRef.current = false;
    // Local expiry backstop (lazy auto-deny sends nothing at 10s); +500ms so
    // a broadcast racing the deadline still wins.
    clearTimeout(askExpireTimer.current);
    askExpireTimer.current = setTimeout(() => settleAsk(false), REMOTE_REQUEST_PROMPT_MS + 500);
    chatGameApi.watchParty(gameId, playerId, 'request_remote')
      .then((res) => {
        if (res && res.busy) {
          askedRef.current = false;
          clearTimeout(askExpireTimer.current);
          clearTimeout(askFlashTimer.current);
          setAskFlash('Someone already asked');
          askFlashTimer.current = setTimeout(() => setAskFlash(null), 2500);
        }
      })
      .catch(() => {
        askedRef.current = false;
        clearTimeout(askExpireTimer.current);
      });
  }, [gameId, playerId, settleAsk]);

  // Resolve our ask from party state: we became holder = granted (silent);
  // our parked request vanished or got replaced = denied / expired → toast.
  // A null pending BEFORE ours was ever seen parked just means our claim's
  // broadcast hasn't landed yet — the timer covers that gap.
  const pendingRequest = watchParty?.pending_request || null;
  useEffect(() => {
    if (!askedRef.current) return;
    if (isRemoteHolder) { settleAsk(true); return; }
    const mine = pendingRequest && pendingRequest.player_id === playerId;
    if (mine) { askSeenRef.current = true; return; }
    if (pendingRequest || askSeenRef.current) settleAsk(false);
  }, [pendingRequest, isRemoteHolder, playerId, settleAsk]);

  const queueAddInput = (watchParty.screening === true) ? null : (
    // 🔴 flexShrink: 0 is LOAD-BEARING (Nina 08-08: "the paste to queue on
    // desktop is really hard to see — where are you supposed to paste to?").
    // This sits below a `flex: 1` queue list inside a rail with
    // `overflow: hidden`, under a row-level maxHeight from the stage resize.
    // Without it the input's default flex-shrink:1 let the squeeze crush it to
    // zero height and the rail clipped it away — so while you were seated there
    // was literally nowhere on screen to paste a link.
    <div style={{ padding: '7px 8px', borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
      <input
        value={queueUrl}
        onChange={(e) => setQueueUrl(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submitQueueUrl(); }}
        placeholder="➕ Paste a link to queue…"
        style={{
          width: '100%', boxSizing: 'border-box',
          // 16px floor on compact/touch — smaller fonts make iOS zoom the
          // whole page when the input focuses
          padding: '6px 10px', borderRadius: 999, fontSize: compact ? '16px' : '0.68em',
          // Brighter than the old 0.18/0.07 pair (08-08): against the rail's
          // near-black it read as part of the panel, not as a field you type in.
          border: '1px solid rgba(206,147,216,0.55)',
          backgroundColor: 'rgba(255,255,255,0.18)',
          color: '#fff', outline: 'none',
        }}
      />
      {queueFlash && (
        <div style={{ fontSize: '0.6em', fontWeight: 700, color: '#ce93d8', padding: '4px 4px 0' }}>
          {queueFlash}
        </div>
      )}
    </div>
  );

  // ── Reaction bullets floating over the video (AI quips + human emojis) ──
  const [bullets, setBullets] = useState([]);
  const bulletIdRef = useRef(0);
  // 🎬🍿 09-22 show night (Nina, mid-film: "a dog keeps repeating 'woof! still
  // moving? told ya' as a quip … after someone else does a quip", then her own
  // line did the same). The removal timer used to live in this effect's closure
  // and the effect's CLEANUP cleared it — so any reaction arriving inside a
  // bullet's lifetime (a quip, an emoji, a mirrored ghost line) cancelled the
  // PREVIOUS bullet's removal. That bullet then stayed in state forever at
  // opacity 0 (wpQuipOut fills forwards) and re-played its whole in/out
  // animation every time its DOM node was re-inserted (the theater host node is
  // re-parented between slots with a raw appendChild). Verified server-side:
  // a 6-minute tap of the live socket delivered every quip exactly once.
  // Two changes: timers live in a ref map, cleared only on unmount; and every
  // add prunes anything past its lifetime, so a stale bullet cannot survive.
  const bulletTimersRef = useRef(new Map());
  useEffect(() => { injectBulletStyles(); }, []);
  useEffect(() => () => {
    for (const t of bulletTimersRef.current.values()) clearTimeout(t);
    bulletTimersRef.current.clear();
  }, []);
  useEffect(() => {
    if (!lastReaction) return;
    const id = ++bulletIdRef.current;
    const durationS = lastReaction.kind === 'quip' ? 10 : 4.5;
    const lifeMs = durationS * 1000 + 500;
    const now = Date.now();
    setBullets(prev => [...prev.filter(b => (now - (b.at || 0)) < (b.lifeMs || 0)).slice(-11), {
      id,
      kind: lastReaction.kind,
      emoji: lastReaction.emoji,
      text: lastReaction.text,
      sender_name: lastReaction.sender_name,
      avatar_url: lastReaction.avatar_url || null,
      left: 8 + Math.random() * 84,          // emoji risers spread across the width
      durationS,
      at: now,
      lifeMs,
    }]);
    const timer = setTimeout(() => {
      bulletTimersRef.current.delete(id);
      setBullets(prev => prev.filter(b => b.id !== id));
    }, lifeMs);
    bulletTimersRef.current.set(id, timer);
  }, [lastReaction]);

  // ── One reconciliation pass: player ↔ authoritative party state ─────────
  // Handles video changes (everyone — the holder's player must follow an
  // advance too), then clock drift and play/pause state (non-holders only:
  // the holder's player IS the source of truth, never correct it).
  // Set before OUR OWN pauseVideo() calls so onStateChange can tell a
  // reconcile-initiated pause from the user tapping the video surface
  // (controls:0 still allows click-to-pause on the iframe).
  const programmaticPauseRef = useRef(false);
  const reconcileToParty = useCallback(() => {
    const p = playerRef.current;
    const wp = wpRef.current;
    if (!p || !playerReadyRef.current || !wp) return;
    try {
      // Video changed (skip / queue advance)
      if (wp.video_id !== loadedVideoIdRef.current) {
        loadedVideoIdRef.current = wp.video_id;
        lastBeatPosRef.current = null;
        // The party moved on — free-roamers rejoin on the new video
        freeRoamRef.current = false;
        setFreeRoam(false);
        p.loadVideoById(wp.video_id, Math.floor(estimatePosition(wp)));
        return;
      }
      if (isHolderRef.current) return;
      // 🎬🍿 SEATING: a screening's anchor may be a few minutes in the FUTURE.
      // estimatePosition clamps that to 0, which alone would play 0-10s on a
      // loop; hold the first frame instead, and let the countdown ticker below
      // call back here the second the anchor passes.
      if (wp.screening && new Date(wp.position_at).getTime() > Date.now()) {
        const st0 = p.getPlayerState();
        if (st0 === (window.YT?.PlayerState || {}).PLAYING) { programmaticPauseRef.current = true; p.pauseVideo(); }
        if ((p.getCurrentTime() || 0) > 1) p.seekTo(0, true);
        return;
      }
      // 🧭 Free roam (scrub decouple, Nina 07-13): a viewer who deliberately
      // paused/scrubbed is exploring — the 10s self-heal leaves them alone
      // until Catch up (or a video change) rejoins them to the party clock.
      if (freeRoamRef.current) return;
      const YTstate = window.YT?.PlayerState || {};
      const st = p.getPlayerState();
      if (st === YTstate.BUFFERING) return; // mid-ad/buffer — don't fight it
      if (wp.state === 'paused') {
        if (st === YTstate.PLAYING) { programmaticPauseRef.current = true; p.pauseVideo(); }
        else if (st !== YTstate.PAUSED) return; // unstarted/cued: nothing to park
      } else if (st !== YTstate.PLAYING && st !== YTstate.ENDED) {
        // PAUSED, CUED, or UNSTARTED (-1, no named constant) while the party
        // plays: resume. Covers blocked autoplay and stalled loads — a no-op
        // if the browser still refuses (the Catch up chip is the manual out).
        // ENDED stays ended: playVideo() there restarts from 0, and with the
        // live target still past the end that's a replay loop.
        p.playVideo();
      }
      const target = estimatePosition(wp);
      const current = p.getCurrentTime() || 0;
      if (st === YTstate.ENDED && target >= current) return; // can't sync past the end
      // Never SEEK past the end (07-16 replay-loop audit): host-mode parties
      // have no heartbeats, so a stalled couch's party clock can pass a
      // clip's duration — seeking there makes YouTube fire ENDED, which this
      // couch then reports, force-advancing the whole room. A couch actually
      // playing reaches the real end and reports it naturally.
      const dur = p.getDuration() || 0;
      if (dur > 0 && target >= dur - 1) return;
      if (Math.abs(current - target) > DRIFT_TOLERANCE_S) {
        p.seekTo(target, true);
      }
    } catch (e) { /* player mid-transition */ }
  }, []);

  // 🎬🍿 SEATING countdown (09-20): whole seconds until a screening's anchor.
  // 0 for every ordinary party (their anchor is never in the future). The
  // second it reaches 0 the reconcile runs, so the film starts on the beat
  // instead of up to 10s late on the interval.
  const seatingAnchor = (watchParty.screening === true && watchParty.position_at) ? new Date(watchParty.position_at).getTime() : 0;
  const [seatingLeft, setSeatingLeft] = useState(() => Math.max(0, Math.ceil((seatingAnchor - Date.now()) / 1000)));
  useEffect(() => {
    if (!seatingAnchor || seatingAnchor <= Date.now()) { setSeatingLeft(0); return undefined; }
    const tick = () => {
      const left = Math.max(0, Math.ceil((seatingAnchor - Date.now()) / 1000));
      setSeatingLeft(left);
      // every second while seating: the autoplaying player is parked on frame 0
      // within a beat of mounting (never 10s of stray sound), then released at 0
      reconcileToParty();
      if (left === 0) clearInterval(id);
    };
    const id = setInterval(tick, 1000);
    tick();
    return () => clearInterval(id);
  }, [seatingAnchor, reconcileToParty]);

  // ── Create the player once ────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    loadYouTubeApi().then((YT) => {
      if (cancelled || !hostRef.current || playerRef.current) return;
      const wp = wpRef.current;
      if (!wp) return;
      loadedVideoIdRef.current = wp.video_id;
      playerRef.current = new YT.Player(hostRef.current, {
        videoId: wp.video_id,
        playerVars: {
          autoplay: 1,
          mute: (ambient && !audibleStartRef.current) ? 1 : 0,
          start: Math.floor(estimatePosition(wp)),
          playsinline: 1,
          rel: 0,
          // Viewers get no YouTube-native controls — the party remote is the remote
          controls: 0,
          disablekb: 1,
        },
        events: {
          onReady: (e) => {
            playerReadyRef.current = true;
            setDuration(e.target.getDuration() || 0);
            if (ambient && !audibleStartRef.current) { try { e.target.mute(); } catch (err) { /* noop */ } }
            // A pill tap that beat the boot: the gesture happened, deliver the
            // sound now (after the ambient mute above, so the intent wins).
            if (pendingUnmuteRef.current) {
              pendingUnmuteRef.current = false;
              try { e.target.unMute(); } catch (err) { /* noop */ }
            }
            if (wpRef.current?.state === 'paused') {
              programmaticPauseRef.current = true;
              e.target.pauseVideo();
            } else {
              e.target.playVideo();
            }
            // Audible-start attempt (curated ambient + every desktop seat):
            // if the browser blocked audible autoplay the player just sits
            // UNSTARTED — detect that and degrade to a muted start. Mobile
            // shows the "tap to join" overlay; desktop pulses the corner 🔊
            // (needsUnmute drives both renders).
            if (audibleStartRef.current) {
              setTimeout(() => {
                try {
                  const YTstate = window.YT?.PlayerState || {};
                  const st = e.target.getPlayerState();
                  if (wpRef.current?.state !== 'paused'
                      && st !== YTstate.PLAYING && st !== YTstate.BUFFERING) {
                    e.target.mute();
                    e.target.playVideo();
                    setNeedsUnmute(true);
                    setMuted(true);
                  }
                } catch (err) { /* player gone */ }
              }, 1600);
            }
            // The party may have advanced videos or drifted while the player
            // was initializing — the WS pass drops updates that arrive before
            // ready, so run a full pass now.
            reconcileToParty();
          },
          onStateChange: (e) => {
            const YTstate = window.YT?.PlayerState || {};
            // 🧟 Abandoned-party sensor (08-23 rickroll postmortem): the
            // holder left (remote put down, or heartbeat stale > 60s, which
            // mirrors the backend's WATCH_PARTY_REMOTE_STALE_MS — canControl
            // already admits anyone then). Without this, a manual party whose
            // starter closed the tab played its final video "forever": ENDED
            // fired on every couch and no couch was allowed to say so. While
            // the holder is PRESENT their report stays the only one, exactly
            // as before.
            const wpNow = wpRef.current;
            const holderGone = !!wpNow && !wpNow.host_mode && (
              !wpNow.remote_holder ||
              Date.now() - new Date(wpNow.position_at || wpNow.started_at).getTime() > 60 * 1000
            );
            if (e.data === YTstate.ENDED && (isHolderRef.current || wpNow?.host_mode || holderGone)) {
              // Video finished — holder advances; in host-mode ANY couch reports
              // it (idempotent ended_video_id — the AI host has no client).
              // Report the video THIS PLAYER finished, not the party's latest:
              // during an in-flight advance they diverge, and reporting the new
              // id advanced the room a second time (07-16 replay-loop audit).
              onNext && onNext(loadedVideoIdRef.current);
            }
            if (e.data === YTstate.PLAYING) { setLocalState('playing'); setEverPlayed(true); }
            if (e.data === YTstate.PAUSED) {
              setLocalState('paused');
              // A pause WE didn't script while the party plays = the user
              // tapped the video itself to pause (controls:0 still allows
              // it). Without this, the 10s self-heal force-resumed them
              // ("pause only lasts a few seconds", Nina 07-25). Same
              // contract as the ⏸ chip: free roam until Catch up.
              if (programmaticPauseRef.current) {
                programmaticPauseRef.current = false;
              } else if (!isHolderRef.current && wpRef.current?.state !== 'paused') {
                freeRoamRef.current = true;
                setFreeRoam(true);
              }
            }
          },
        },
      });
    });
    return () => {
      cancelled = true;
      try { playerRef.current?.destroy(); } catch (e) { /* already gone */ }
      playerRef.current = null;
      playerReadyRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── React to authoritative state changes (video change, drift, play/pause) ─
  useEffect(() => {
    if (!watchParty) return;
    reconcileToParty();
  }, [watchParty, isRemoteHolder, reconcileToParty]);

  // ── Holder heartbeat (~10s) keeps the authoritative clock fresh ────────
  // Only a player that is genuinely PLAYING or deliberately PAUSED is a
  // trustworthy clock. UNSTARTED / BUFFERING / CUED / ENDED all report
  // getCurrentTime() ≈ 0 — or the OLD video's end during an advance — and
  // one such beat, persisted as "playing @ 0", yanks every couch back to
  // the start on their next drift check (the "first few seconds on repeat"
  // loop). Silence is safe: viewers extrapolate from the last good beat,
  // and 60s+ of silence makes the remote grabbable.
  useEffect(() => {
    if (!isRemoteHolder || !onHeartbeat) return;
    const id = setInterval(() => {
      const p = playerRef.current;
      if (!p || !playerReadyRef.current) return;
      try {
        const YTstate = window.YT?.PlayerState || {};
        const st = p.getPlayerState();
        if (st !== YTstate.PLAYING && st !== YTstate.PAUSED) return;
        const pos = p.getCurrentTime() || 0;
        if (st === YTstate.PLAYING) {
          // A "playing" clock frozen since the last beat is an ad or a stall
          // — re-anchoring position_at to it loops viewers around the freeze
          const last = lastBeatPosRef.current;
          if (last !== null && Math.abs(pos - last) < 0.75) return;
        }
        lastBeatPosRef.current = pos;
        onHeartbeat(pos, st === YTstate.PAUSED ? 'paused' : 'playing', loadedVideoIdRef.current);
      } catch (e) { /* noop */ }
    }, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isRemoteHolder, onHeartbeat]);

  // ── Reconcile loop (~10s, everyone) ─────────────────────────────────────
  // The WS-triggered pass above only runs when the watchParty OBJECT changes
  // — host-mode parties have no heartbeats (state changes only on advance),
  // so between videos NOTHING would fix drift from ads, buffering, or
  // background-tab throttling. It also catches advances that landed while
  // the player was still initializing (the WS pass drops those). Runs for
  // the holder too: reconcileToParty only ever reloads a changed video for
  // them — their clock is otherwise the party's truth.
  useEffect(() => {
    const id = setInterval(reconcileToParty, 10 * 1000);
    return () => clearInterval(id);
  }, [reconcileToParty]);

  // 🔊 Icon truth-sync (Nina 07-16): several paths mute/unmute the player
  // without touching React state (playerVars, onReady mute, curated fallback,
  // video reloads) — poll the player so the icon can never show the wrong
  // state for more than a beat.
  useEffect(() => {
    const id = setInterval(() => {
      // Display-only poll: skip while the tab is hidden or the theater is
      // parked in the display:none holder (offsetParent null there — root is
      // in-flow). Each tick is a cross-origin postMessage into the YT iframe;
      // the icon self-heals within 2s of re-show. Heartbeat/reconcile are NOT
      // gated this way — they are the party clock. (perf 08-13)
      if (document.hidden || (rootRef.current && rootRef.current.offsetParent === null)) return;
      const p = playerRef.current;
      if (!p || !playerReadyRef.current) return;
      try {
        const m = p.isMuted();
        if (typeof m === 'boolean') setMuted(prev => (prev === m ? prev : m));
      } catch (e) { /* player mid-transition */ }
    }, 2000);
    return () => clearInterval(id);
  }, []);

  // Manual "catch up" — jump straight to the party's live position
  const handleCatchUp = useCallback(() => {
    const p = playerRef.current;
    const wp = wpRef.current;
    if (!p || !playerReadyRef.current || !wp) return;
    freeRoamRef.current = false;
    setFreeRoam(false);
    try {
      const target = estimatePosition(wp);
      p.seekTo(target, true);
      setDisplayPos(target);
      if (wp.state === 'playing') p.playVideo();
    } catch (e) { /* player mid-transition */ }
  }, []);

  // ── 1s display ticker (progress bar + clock) ───────────────────────────
  useEffect(() => {
    const id = setInterval(() => {
      // Display-only: no clock/progress to advance while hidden — this tick
      // was a full theater re-render + an iframe postMessage every second
      // for the whole life of a seated party, including parked off-screen
      // in the hidden holder. Resumes within 1s of re-show. (perf 08-13)
      if (document.hidden || (rootRef.current && rootRef.current.offsetParent === null)) return;
      const p = playerRef.current;
      if (p && playerReadyRef.current) {
        try {
          setDisplayPos(p.getCurrentTime() || 0);
          const d = p.getDuration();
          if (d) setDuration(d);
        } catch (e) { /* noop */ }
      }
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // ── Holder controls ─────────────────────────────────────────────────────
  // Controls carry the video they were aimed at — the server drops a control
  // that raced a queue advance instead of grafting it onto the new video.
  const handlePlayPause = useCallback(() => {
    const p = playerRef.current;
    if (!p || !playerReadyRef.current) return;
    const nextState = localState === 'playing' ? 'paused' : 'playing';
    const pos = p.getCurrentTime() || 0;
    if (nextState === 'paused') p.pauseVideo(); else p.playVideo();
    setLocalState(nextState);
    onControl && onControl(nextState, pos, loadedVideoIdRef.current);
  }, [localState, onControl]);

  const handleSeek = useCallback((e) => {
    const p = playerRef.current;
    if (!p || !playerReadyRef.current) return;
    const pos = Number(e.target.value);
    p.seekTo(pos, true);
    setDisplayPos(pos);
    onControl && onControl(localState, pos, loadedVideoIdRef.current);
  }, [localState, onControl]);

  if (!watchParty) return null;

  const accent = THEME.accent || '#7b1fa2';
  const queue = watchParty.queue || [];
  // Sync indicator: how far this couch is from the party's live position
  // (displayPos ticks every 1s, so this stays fresh without extra state)
  const liveDrift = isRemoteHolder ? 0 : Math.abs(displayPos - estimatePosition(watchParty));
  const inSync = liveDrift <= 4;
  // Remote is up for grabs when the AI host holds it (always — the host never
  // objects), nobody holds it (the holder put it down), or a human holder has
  // gone quiet for 65s+ (closed tab, bailed).
  // The 1s display ticker keeps this evaluation fresh.
  // 🎬🍿 09-20 SCREENING dress: the projector is locked server-side (houseScreening
  // lockReason), so the chrome that would only ever answer "locked" is hidden.
  // Pause for yourself, the scrub, mute and ⟲ Catch up all stay: they were always local.
  const screening = watchParty.screening === true;
  const remoteGrabbable = !screening && !isRemoteHolder && isPlayer && (
    !watchParty.remote_holder
      ? true
      : watchParty.host_mode
        ? watchParty.remote_holder === 'host'
        : Date.now() - new Date(watchParty.position_at || watchParty.started_at).getTime() > 65 * 1000
  );
  // Not grabbable + a holder exists = a HUMAN is actively driving → the
  // polite path: Ask for it (Arc 5). The 1s ticker keeps the 10s freshness
  // checks below live without extra state.
  const humanHoldsRemote = !screening && isPlayer && !isRemoteHolder && !remoteGrabbable && !!watchParty.remote_holder;
  const pendingFresh = pendingRequest &&
    Date.now() - Number(pendingRequest.at) < REMOTE_REQUEST_PROMPT_MS;
  const myAskPending = !!(pendingFresh && pendingRequest.player_id === playerId);
  // Holder's compact prompt: hides at 10s (auto-deny); server keeps a 2s
  // skew grace before refusing the grant outright.
  const holderPrompt = isRemoteHolder && pendingFresh ? pendingRequest : null;

  return (
    <div ref={rootRef} style={{
      backgroundColor: '#000',
      borderRadius: isMobile ? 0 : '10px',
      overflow: 'hidden',
      // 🗜️ Flex column (Nina 08-27): the desktop center column squeezes this
      // root (everything below the stage refuses to shrink), and as a plain
      // block it clipped its BOTTOM children — queue drawer, paste input,
      // controls — exactly the narrow-desktop "can't queue anything" bug.
      // As a column with every chrome row flexShrink:0, the squeeze routes to
      // the player row instead (video shrinks, chrome stays reachable). On
      // mobile nothing applies height pressure, so this is a no-op there.
      display: 'flex', flexDirection: 'column', minHeight: 0,
      // Holding the remote is unmissable: the whole theater frame glows in the
      // accent (static double glow — no animation, so it costs nothing).
      border: isRemoteHolder ? `2px solid ${accent}` : `1px solid ${THEME.borderLight || 'rgba(255,255,255,0.15)'}`,
      marginBottom: '10px',
      boxShadow: isRemoteHolder
        ? `0 0 14px ${accent}88, 0 6px 24px rgba(0,0,0,0.45)`
        : '0 6px 24px rgba(0,0,0,0.45)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        // Compact: buttons (Queue/⌄/Leave) wrap to their own row — sharing one
        // row starved the title to a ~50px column that waterfalled letter by
        // letter (Nina's iPhone-mini screenshot, 07-20)
        flexWrap: compact ? 'wrap' : 'nowrap',
        flexShrink: 0,
        padding: '8px 12px',
        background: 'linear-gradient(90deg, rgba(123,31,162,0.35), rgba(0,0,0,0.4))',
        color: '#fff',
      }}>
        <span style={{ fontSize: '1.1em' }}>🍿</span>
        <div style={{ flex: compact ? '1 1 calc(100% - 30px)' : '1 1 0%', minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.85em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {/* Compact: the prefix ate half the width and truncated the actual
                title to 5 chars (07-15 screenshot). The 🍿 carries the context. */}
            {compact ? watchParty.title : `NOW PLAYING · ${watchParty.title}`}
          </div>
          <div style={{ fontSize: '0.7em', opacity: 0.7, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {watchParty.host_mode
              ? <>📺 {watchParty.started_by_name || 'The Host'}&rsquo;s programming · always on</>
              : <>started by {watchParty.started_by_name || 'someone'}</>}
          </div>
        </div>
        {/* Holder pill — pairs with the frame glow so there's zero doubt who
            drives (the old subtitle mention was easy to miss). */}
        {isRemoteHolder && (
          <span style={{
            flexShrink: 0, padding: '3px 9px', borderRadius: 999,
            background: accent, color: '#fff', fontSize: '0.66em', fontWeight: 800,
            letterSpacing: '0.5px', textTransform: 'uppercase', whiteSpace: 'nowrap',
          }}>
            {/* Short label on narrow containers — the long pill starved the title */}
            {compact ? '🎮 Remote' : '🎮 You have the remote'}
          </span>
        )}
        {compact && (
          <button
            onClick={() => setShowQueue(!showQueue)}
            style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', borderRadius: 6, padding: '7px 10px', cursor: 'pointer', fontSize: '0.75em' }}
          >
            Queue{queue.length > 0 ? ` (${queue.length})` : ''}
          </button>
        )}
        {isRemoteHolder ? (
          <button
            onClick={() => { if (window.confirm('End the watch party for everyone?')) onStop && onStop(); }}
            title="End party for everyone"
            /* marginLeft auto (Nina 08-03): exits live on the RIGHT edge of the
               row; Queue stays left. Same for Leave couch below. */
            style={{ marginLeft: 'auto', background: 'rgba(255,80,80,0.25)', border: 'none', color: '#fff', borderRadius: 6, padding: compact ? '7px 10px' : '4px 8px', cursor: 'pointer', fontSize: '0.75em' }}
          >
            ✕ End
          </button>
        ) : (
          <button
            onClick={() => onExit && onExit()}
            title="Leave the couch (collapses the party for you; it keeps playing for everyone else)"
            /* Bigger + bordered (Nina 07-25: "more noticeable, no glow effects") —
               static contrast only, no animation/shadow so nothing repaints. */
            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.16)', border: '1.5px solid rgba(255,255,255,0.55)', color: '#fff', borderRadius: 8, padding: compact ? '9px 14px' : '7px 14px', cursor: 'pointer', fontSize: '0.85em', fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            Leave couch
            {/* The ⌄ is the "this collapses you out" hint (Nina 07-26): its
                own glyph, bigger and clear of the text — inline it read as a
                typo instead of an affordance. One action, so no divider —
                a split look would promise a second dropdown action. */}
            <span aria-hidden style={{ fontSize: '1.35em', fontWeight: 900, lineHeight: 1, transform: 'translateY(1px)' }}>⌄</span>
          </button>
        )}
        {/* ⌄ minimize rides RIGHT of Leave/End (Nina 07-21; was left of it —
            and duplicated the stage header's ⌄, which now hides while a
            party holds the stage). Shrinks the theater, keeps it playing. */}
        {onMinimize && (
          <button
            onClick={() => onMinimize()}
            title="Shrink the theater (keeps playing)"
            aria-label="Minimize theater"
            style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', borderRadius: 6, padding: compact ? '7px 12px' : '4px 10px', cursor: 'pointer', fontSize: '0.85em', lineHeight: 1 }}
          >
            ⌄
          </button>
        )}
      </div>

      {/* Player row — video left, YouTube-style "Up next" rail right (desktop).
          The video box caps its width at its clamped height × 16/9 so wide
          columns get a centered true-16:9 frame instead of a pillarboxed
          strip; justifyContent centers video+rail as a group in the gutter. */}
      <div style={{
        display: 'flex', alignItems: 'stretch',
        // The drag cap governs the whole ROW (Nina 07-25: dragging the resize
        // bar up shrank only the video and left a black gap — the Up-next
        // rail's intrinsic content height was holding the row open). The rail
        // scrolls/clips inside the cap instead. Capless desktop (the pinned
        // default since 08-12) gets the same guard at the video's own 55dvh
        // budget; mobile stays exactly as before.
        maxHeight: rowCap, // 🎬 cinema-aware (see `cinema` above)
        // 🎬📐 09-22 late: a video FLOOR on desktop. This root is a flex column
        // whose chrome rows refuse to shrink, so a squeezed center column used
        // to route ALL of the loss into this row (measured 21px tall at a
        // 1280x720 window, on the 09-20 build too). The page now yields first
        // (GameStepPage theaterHero: masthead fold + lower feed floor) and this
        // floor guarantees a watchable picture on short windows. Phones keep 0.
        minHeight: isMobile ? 0 : 'min(180px, 25dvh)',
        // Contain any residual video spill when the root's flex squeeze lands
        // here (08-27) — never paint over the queue drawer/controls below.
        overflow: 'hidden',
      }}>
        {/* Video centers in its OWN slack (Nina 08-10: "too horizontally
            crowded") — justifyContent:center on the row glued video+rail
            into one centered group, dead black on BOTH outer edges and the
            rail hugging the video. Now the rail pins to the row's right
            edge and the video floats centered in what's left. */}
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', justifyContent: 'center' }}>
        <div style={{
          position: 'relative', width: '100%', minWidth: 0, aspectRatio: '16 / 9',
          // Desktop 100% (08-27): when the row's height is definite (55dvh /
          // drag cap / the root's flex squeeze) the video caps at the ROW, so
          // the queue input + controls stay visible; when the row is content-
          // sized a %-max-height can't resolve and is ignored = the old
          // behavior. The YT iframe pillarboxes internally. Mobile keeps 40dvh.
          maxHeight: isMobile ? '40dvh' : '100%',
          maxWidth: videoCapW, // 🎬 cinema-aware (see `cinema` above)
          backgroundColor: '#000',
        }}>
          <div ref={hostRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
          {seatingLeft > 0 && (
            <div data-wp-seating="1" style={{
              position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
              background: 'rgba(12,8,20,0.92)', color: '#ffd98a', textAlign: 'center', padding: 12, boxSizing: 'border-box', pointerEvents: 'none',
            }}>
              <span style={{ fontSize: 'clamp(15px, 2.4vw, 24px)', fontWeight: 800, overflowWrap: 'anywhere' }}>🎬 {watchParty.title}</span>
              <span style={{ fontSize: 'clamp(13px, 1.8vw, 17px)', color: '#efe4d2', fontVariantNumeric: 'tabular-nums' }}>
                Starting in {Math.floor(seatingLeft / 60)}:{String(seatingLeft % 60).padStart(2, '0')}
              </span>
            </div>
          )}

          {/* Soft ask-outcome toast (deny or 10s of holder silence) — quiet
              by design: no chat line, nothing public, just this whisper */}
          {remoteToast && (
            <div style={{
              position: 'absolute', left: '50%', bottom: 12, transform: 'translateX(-50%)',
              zIndex: 5, pointerEvents: 'none', whiteSpace: 'nowrap',
              background: 'rgba(0,0,0,0.75)', color: '#fff', borderRadius: 999,
              padding: '6px 14px', fontSize: '0.72em', fontWeight: 700,
            }}>
              {remoteToast}
            </div>
          )}

          {/* Ambient auto-seat starts muted — this click is the autoplay
              gesture. MOBILE ONLY since 08-12 (Nina: no separate "tap to join"
              + mute control): desktop joins with sound on the seat click and
              falls back to the pulsing corner 🔊 when the browser blocks it. */}
          {needsUnmute && isMobile && (
            <button
              onClick={() => {
                // Record intent FIRST — if the player isn't booted yet the
                // calls below no-op and onReady delivers the unmute instead
                // (pendingUnmuteRef). Before this, an early tap dismissed the
                // pill without ever unmuting (desktop auto-seat trap, 08-03).
                pendingUnmuteRef.current = true;
                try {
                  if (playerReadyRef.current && playerRef.current) {
                    playerRef.current.unMute();
                    playerRef.current.playVideo();
                    pendingUnmuteRef.current = false;
                  }
                } catch (e) { /* player mid-load — onReady applies the intent */ }
                setNeedsUnmute(false);
                setMuted(false);
              }}
              style={{
                position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
                zIndex: 4, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '12px 22px', borderRadius: 999,
                border: '1px solid rgba(186,104,200,0.6)',
                background: 'linear-gradient(135deg, rgba(30,10,45,0.95), rgba(70,20,110,0.95))',
                color: '#fff', fontWeight: 700, fontSize: '0.95em',
                boxShadow: '0 8px 26px rgba(0,0,0,0.55)',
              }}
            >
              🍿 Tap to join the party
            </button>
          )}

          {/* 🔊 Local sound toggle — top-left overlay (Nina 07-16: was buried in
              the controls row and its icon drifted from the real player state).
              Icon truth-syncs from the player below, so it can't lie. */}
          <button
            onClick={() => {
              const p = playerRef.current;
              if (!p || !playerReadyRef.current) return;
              try {
                if (p.isMuted()) { p.unMute(); setMuted(false); setNeedsUnmute(false); }
                else { p.mute(); setMuted(true); }
              } catch (e) { /* player mid-transition */ }
            }}
            title={muted ? 'Sound on' : 'Sound off (just for you)'}
            style={{
              position: 'absolute', top: 8, left: 8, zIndex: 4,
              background: 'rgba(15,5,25,0.7)', border: '1px solid rgba(255,255,255,0.25)',
              color: '#fff', borderRadius: '50%', width: 34, height: 34,
              cursor: 'pointer', fontSize: '0.9em', flexShrink: 0,
              // 🔊 Desktop blocked-start nudge (Nina 08-12): the center "Tap to
              // join the party" overlay is gone on desktop — ONE sound control.
              // When the audible start got blocked, this corner toggle pulses
              // until tapped (the 08-03 lesson: a quiet 34px corner button
              // alone was a one-way door — the pulse is the discoverability).
              ...(needsUnmute && !isMobile ? { border: '1px solid rgba(206,147,216,0.9)' } : {}),
            }}
            className={needsUnmute && !isMobile ? 'wp-sound-nudge' : undefined}
          >
            {muted ? '🔇' : '🔊'}
          </button>

          {/* Quip stream — subtitle-style lower third (Nina 08-10: the boxed
              bubbles read crowded — "I don't think they need a container").
              No card chrome: bare text runs most of the video's width and
              text-shadow alone carries legibility over any frame, the way
              broadcast captions do. */}
          <div style={{
            // 🎬🍿 09-22 show night (Nina, mid-film screenshot): YouTube draws its
            // closed captions and the paused-state "More videos" strip in the
            // bottom ~20% of the frame, on top of quips anchored at bottom: 8.
            // Anchor the couch a quarter of the way up instead: clear of CC,
            // clear of the strip, still low enough to read as the couch.
            position: 'absolute', left: 10, bottom: '24%', zIndex: 3,
            width: isMobile ? '86%' : 'min(78%, 580px)',
            display: 'flex', flexDirection: 'column', gap: 5,
            pointerEvents: 'none',
          }}>
            {bullets.filter(b => b.kind === 'quip').slice(-4).map(b => (
              <div
                key={b.id}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 7,
                  color: '#fff', fontSize: isMobile ? '0.76em' : '0.85em',
                  lineHeight: 1.35,
                  textShadow: '0 1px 3px rgba(0,0,0,0.95), 0 0 12px rgba(0,0,0,0.7)',
                  animation: `wpQuipIn 0.25s ease, wpQuipOut 0.6s ease ${b.durationS - 0.6}s forwards`,
                }}
              >
                {b.avatar_url ? (
                  <img src={b.avatar_url} alt="" style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, marginTop: 1, boxShadow: '0 1px 4px rgba(0,0,0,0.7)' }} />
                ) : (
                  <span style={{ flexShrink: 0, filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.8))' }}>🛋️</span>
                )}
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontWeight: 800, color: '#ce93d8', marginRight: 5 }}>{b.sender_name}</span>
                  {b.text}
                </span>
              </div>
            ))}
          </div>

          {/* Emoji risers — spread across the width, floating up */}
          <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 3 }}>
            {bullets.filter(b => b.kind !== 'quip').map(b => (
              <div
                key={b.id}
                style={{
                  position: 'absolute',
                  left: `${b.left}%`,
                  bottom: 12,
                  fontSize: isMobile ? '1.6em' : '2em',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                  animation: `wpEmojiRise ${b.durationS}s ease-out forwards`,
                }}
              >
                {b.emoji}
              </div>
            ))}
          </div>
        </div>
        </div>

        {/* 🛋️ 09-23 batch 1 (MOVIE_NIGHT_UX_HANDOFF §3.4, rulings 9-10): during
            a screening the up-next rail is folded (the projector is locked), so
            its slot holds THE COUCH: the page-level quip log + "Say something to
            the couch". Compact widths keep the drawer; the couch shows under the
            film on phones instead. Null = the slot stays empty, exactly as the
            09-22 cinema left it. */}
        {!compact && cinema && rail && (
          <div data-couch-rail="1" style={{ width: 230, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
            {rail}
          </div>
        )}
        {/* Up next — YouTube-style autoplay rail (wide containers only; compact
            widths keep the drawer so the rail never starves the video).
            Renders even with an empty queue so there's always a place to add. */}
        {!compact && !cinema && (
          <div style={{
            // 216 → 156 (Nina 08-08, "narrow fixed rail") → 190 (Nina 08-10:
            // paste field "more visible, a little longer"). The lever still
            // holds where the video's maxWidth cap isn't binding: the video
            // box derives height from the WIDTH the rail leaves it, so every
            // rail px costs 9/16 px of video height there. 190 buys a usable
            // paste field; compact (<700px container) folds the rail away
            // before it can starve the player.
            width: 190, flexShrink: 0, display: 'flex', flexDirection: 'column',
            minHeight: 0, overflow: 'hidden',
            backgroundColor: 'rgba(8,8,12,0.96)', borderLeft: '1px solid rgba(255,255,255,0.08)',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}>
              <span style={{ fontSize: '0.68em', fontWeight: 800, letterSpacing: '1px', color: '#fff', textTransform: 'uppercase' }}>
                Up next
              </span>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '6px 8px' }}>
              {queue.length === 0 && (
                <div style={{ fontSize: '0.64em', color: 'rgba(255,255,255,0.5)', padding: '10px 4px', lineHeight: 1.4 }}>
                  Nothing queued yet. Add something below!
                </div>
              )}
              {queue.map((q, i) => (
                <div key={`${q.video_id}-${i}`} style={{ display: 'flex', gap: 8, padding: '5px 2px', alignItems: 'flex-start' }}>
                  <div style={{ position: 'relative', width: 78, flexShrink: 0, borderRadius: 6, overflow: 'hidden', backgroundColor: '#000' }}>
                    <div style={{ width: '100%', paddingTop: '56.25%' }} />
                    {q.thumbnail && (
                      <img src={q.thumbnail} alt="" loading="lazy" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                    )}
                    <span style={{
                      position: 'absolute', bottom: 2, right: 3, fontSize: '0.55em', fontWeight: 800,
                      padding: '1px 4px', borderRadius: 4, backgroundColor: 'rgba(0,0,0,0.8)', color: '#fff',
                    }}>{i + 1}</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '0.68em', fontWeight: 600, color: '#fff', lineHeight: 1.3,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}>{q.title || q.url}</div>
                    <div style={{ fontSize: '0.58em', color: 'rgba(255,255,255,0.55)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {q.added_by_name || watchParty.started_by_name || 'the host'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {queueAddInput}
          </div>
        )}
      </div>

      {/* Queue drawer (compact widths) — list + the same add-a-link input as the rail */}
      {compact && showQueue && (
        <div style={{ backgroundColor: 'rgba(0,0,0,0.75)', color: '#fff', fontSize: '0.75em', flexShrink: 0 }}>
          <div style={{ padding: '6px 12px', maxHeight: 120, overflowY: 'auto' }}>
            {queue.length === 0 && (
              <div style={{ padding: '3px 0', opacity: 0.6 }}>Nothing queued yet. Add something below!</div>
            )}
            {queue.map((q, i) => (
              <div key={`${q.video_id}-${i}`} style={{ padding: '3px 0', opacity: 0.9, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {i + 1}. {q.title} <span style={{ opacity: 0.6 }}>· {q.added_by_name}</span>
              </div>
            ))}
          </div>
          {queueAddInput}
        </div>
      )}

      {/* Controls row — wraps instead of clipping: the outer container is
          overflow:hidden, so without wrap the emoji bar / host label were
          silently cut off at narrow widths */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, rowGap: 6, padding: '8px 12px', backgroundColor: 'rgba(0,0,0,0.85)', color: '#fff', flexShrink: 0 }}>
        {isRemoteHolder ? (
          <>
            <button
              onClick={handlePlayPause}
              style={{ background: accent, border: 'none', color: '#fff', borderRadius: '50%', width: 34, height: 34, cursor: 'pointer', fontSize: '1em', flexShrink: 0 }}
            >
              {localState === 'playing' ? '⏸' : '▶︎'}
            </button>
            {/* Compact: one combined clock + the slider on its own full row —
                the split time|slider|time layout crushed the scrubber on
                phones (user 07-15: watch party bad on small screens) */}
            <span style={{ fontSize: '0.75em', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
              {formatTime(displayPos)}{compact ? ` / ${formatTime(duration)}` : ''}
            </span>
            <input
              type="range"
              min={0}
              max={Math.max(1, Math.floor(duration))}
              value={Math.floor(displayPos)}
              onChange={handleSeek}
              style={{ flex: 1, accentColor: accent, cursor: 'pointer', ...(compact ? { flexBasis: '100%', order: 10 } : {}) }}
            />
            {!compact && (
              <span style={{ fontSize: '0.75em', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{formatTime(duration)}</span>
            )}
            <button
              onClick={() => onNext && onNext()}
              title={queue.length ? 'Next in queue' : 'End of queue — ends the party'}
              style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', borderRadius: 6, padding: compact ? '7px 12px' : '5px 9px', cursor: 'pointer', fontSize: '0.8em', flexShrink: 0 }}
            >
              ⏭
            </button>
            {/* Put the remote down — non-destructive exit from holding: the AI
                host takes it back (host mode) or it sits on the couch for
                anyone to grab. Calls the API directly (gameId/playerId are
                already props, like ReportFlag) — parent state syncs via the
                watch_party_state broadcast. Non-fatal: a failed release just
                leaves you holding. */}
            {gameId && playerId && (
              <button
                onClick={() => { chatGameApi.watchParty(gameId, playerId, 'release').catch(() => {}); }}
                title="Put the remote down so anyone can grab it"
                style={{
                  flexShrink: 0, border: `1px solid ${accent}`, borderRadius: 999,
                  padding: compact ? '7px 11px' : '3px 9px', fontSize: '0.68em', fontWeight: 800,
                  cursor: 'pointer', backgroundColor: 'transparent', color: '#fff',
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                }}
              >
                <RemoteIcon />{compact ? 'Put down' : 'Put it down'}
              </button>
            )}
            {/* "{name} wants the remote" — the ask prompt. Hides after 10s
                (auto-deny, Nina's call: holder's couch, holder's rules).
                Deny is deliberately QUIET — no chat line, no public shaming.
                Parent state syncs via the watch_party_state broadcast. */}
            {holderPrompt && gameId && playerId && (
              <span style={{
                flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: compact ? '5px 8px' : '3px 8px', borderRadius: 999,
                border: `1px solid ${accent}`, backgroundColor: 'rgba(123,31,162,0.18)',
                fontSize: '0.68em', fontWeight: 700,
              }}>
                <RemoteIcon size={9} />
                <span style={{ whiteSpace: 'nowrap' }}>{holderPrompt.name} wants the remote</span>
                <button
                  onClick={() => { chatGameApi.watchParty(gameId, playerId, 'grant_remote', { requester_id: holderPrompt.player_id }).catch(() => {}); }}
                  style={{
                    border: 'none', borderRadius: 999, padding: compact ? '6px 10px' : '3px 9px',
                    fontSize: '1em', fontWeight: 800, cursor: 'pointer',
                    backgroundColor: accent, color: '#fff', flexShrink: 0,
                  }}
                >
                  {compact ? 'Give' : 'Hand it over'}
                </button>
                <button
                  onClick={() => { chatGameApi.watchParty(gameId, playerId, 'deny_remote').catch(() => {}); }}
                  style={{
                    border: 'none', borderRadius: 999, padding: compact ? '6px 10px' : '3px 9px',
                    fontSize: '1em', fontWeight: 800, cursor: 'pointer',
                    backgroundColor: 'rgba(255,255,255,0.12)', color: '#fff', flexShrink: 0,
                  }}
                >
                  {compact ? 'Keep' : 'Keep it'}
                </button>
              </span>
            )}
          </>
        ) : (
          <>
            {/* 🧭 Local play/pause — YOUR couch only (free roam); the party
                keeps rolling without you until Catch up */}
            <button
              onClick={() => {
                const p = playerRef.current;
                if (!p || !playerReadyRef.current) return;
                try {
                  if (localState === 'playing') { p.pauseVideo(); setLocalState('paused'); }
                  else { p.playVideo(); setLocalState('playing'); }
                  freeRoamRef.current = true;
                  setFreeRoam(true);
                } catch (e) { /* player mid-transition */ }
              }}
              title="Pause just for you (the party keeps going)"
              style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', borderRadius: '50%', width: compact ? 34 : 28, height: compact ? 34 : 28, cursor: 'pointer', fontSize: '0.8em', flexShrink: 0 }}
            >
              {localState === 'playing' ? '⏸' : '▶︎'}
            </button>
            <span style={{ fontSize: '0.75em', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{formatTime(displayPos)} / {formatTime(duration)}</span>
            {/* 🧭 Local seek — scrubbing puts you in free roam */}
            <input
              type="range"
              min={0}
              max={Math.max(1, Math.floor(duration))}
              value={Math.floor(displayPos)}
              onChange={(e) => {
                const p = playerRef.current;
                if (!p || !playerReadyRef.current) return;
                try {
                  const pos = Number(e.target.value);
                  p.seekTo(pos, true);
                  setDisplayPos(pos);
                  freeRoamRef.current = true;
                  setFreeRoam(true);
                } catch (err) { /* player mid-transition */ }
              }}
              title="Scrub just for you (Catch up rejoins the party)"
              style={{ flex: 1, accentColor: freeRoam ? '#ffd98a' : accent, cursor: 'pointer', ...(compact ? { flexBasis: '100%', order: 10 } : {}) }}
            />
            {/* Grab the remote — take manual control (play/pause/seek/skip).
                The control action itself does the takeover server-side. */}
            {remoteGrabbable && (
              <button
                onClick={() => {
                  // Snap our own couch to the live spot first — the moment we
                  // hold the remote, OUR player becomes the party clock, and
                  // grabbing from 40s behind would drag everyone back with us
                  // on the very next heartbeat.
                  handleCatchUp();
                  onControl && onControl(watchParty.state, estimatePosition(watchParty), watchParty.video_id);
                }}
                title="Take the remote — play, pause, seek, and skip for everyone"
                style={{
                  flexShrink: 0, border: `1px solid ${accent}`, borderRadius: 999,
                  padding: compact ? '7px 11px' : '3px 9px', fontSize: '0.68em', fontWeight: 800,
                  cursor: 'pointer', backgroundColor: 'transparent', color: '#fff',
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                }}
              >
                <RemoteIcon />{compact ? 'Grab' : 'Grab the remote'}
              </button>
            )}
            {/* Ask for it — a HUMAN actively holds, so no instant grab: send a
                polite request instead (Arc 5). One ask at a time party-wide;
                a lost race flashes "already asked" here. While OUR ask is
                parked the pill dims to Asked! so it can't be spammed. */}
            {humanHoldsRemote && gameId && playerId && (
              askFlash ? (
                <span style={{ flexShrink: 0, fontSize: '0.68em', fontWeight: 700, color: '#ce93d8' }}>
                  {askFlash}
                </span>
              ) : (
                <button
                  onClick={handleAskForRemote}
                  disabled={myAskPending}
                  title={myAskPending ? 'Asked! Waiting on the holder' : 'Ask the holder to hand the remote over'}
                  style={{
                    flexShrink: 0, border: `1px solid ${accent}`, borderRadius: 999,
                    padding: compact ? '7px 11px' : '3px 9px', fontSize: '0.68em', fontWeight: 800,
                    cursor: myAskPending ? 'default' : 'pointer',
                    backgroundColor: 'transparent', color: '#fff',
                    opacity: myAskPending ? 0.55 : 1,
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                  }}
                >
                  <RemoteIcon />{myAskPending ? 'Asked!' : (compact ? 'Ask' : 'Ask for it')}
                </button>
              )
            )}
            {/* Sync chip — LIVE with the room; free roam / drift = one tap back */}
            {(() => {
              const synced = inSync && !freeRoam;
              return (
            <button
              onClick={synced ? undefined : handleCatchUp}
              title={synced ? 'In sync with the party'
                : freeRoam ? 'Doing your own thing. Tap to rejoin the party!'
                : `${Math.round(liveDrift)}s behind — jump to live`}
              style={{
                flexShrink: 0, border: 'none', borderRadius: 999,
                padding: compact ? '7px 11px' : '3px 9px', fontSize: '0.68em', fontWeight: 800,
                letterSpacing: '0.5px', textTransform: 'uppercase',
                cursor: synced ? 'default' : 'pointer',
                backgroundColor: synced ? 'rgba(255,255,255,0.1)' : accent,
                color: synced ? 'rgba(255,255,255,0.75)' : '#fff',
              }}
            >
              {synced
                ? (watchParty.state === 'paused' ? 'In sync' : <><span style={{ color: '#ef4444' }}>●</span> Live</>)
                : freeRoam ? '🧭 Rejoin the party' : '⟲ Catch up'}
            </button>
              );
            })()}
            {/* Holder-name label is redundant chrome on a cramped row */}
            {!compact && (
              <span style={{ fontSize: '0.7em', opacity: 0.6, display: 'inline-flex', alignItems: 'center', gap: 4 }}><RemoteIcon size={9} /> {isRemoteHolder ? 'you' : (watchParty.started_by_name || 'host')}</span>
            )}
          </>
        )}

        {/* Emoji reactions — everyone gets to heckle */}
        {onReact && (
          <div style={{ display: 'flex', gap: 2, flexShrink: 0, marginLeft: 'auto' }}>
            {WATCH_REACT_EMOJIS.map(emoji => (
              <button
                key={emoji}
                onClick={() => onReact(emoji)}
                title="React"
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  // Compact = touch: BIGGER emoji + padding, not smaller (tap
                  // targets; ~40px tall on phones, mobile audit wave 3)
                  fontSize: '1.15em', padding: compact ? '11px 7px' : '2px 3px',
                  lineHeight: 1, transition: 'transform 0.1s ease',
                }}
                onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(1.35)'; }}
                onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}

        {/* 🛡️ Report the current video (MODERATION_P1) — 3 distinct reporters
            pull it from the party + block it from the rotation */}
        {gameId && watchParty.video_id && (
          <ReportFlag
            gameId={gameId}
            playerId={playerId}
            kind="watch_video"
            targetId={watchParty.video_id}
            compact
          />
        )}
      </div>
    </div>
  );
};

export default WatchPartyTheater;
